import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Clock, User } from "lucide-react";
import SEO from "@/components/common/SEO";
import { Reveal } from "@/components/common/motion";
import { ContactCTA } from "@/components/shared/blocks";
import { blog } from "@/data/site";

export default function BlogPost() {
  const { slug } = useParams();
  const post = blog.find((b) => b.slug === slug);
  if (!post) return <Navigate to="/blog" replace />;
  const related = blog.filter((b) => b.slug !== slug).slice(0, 3);

  return (
    <>
      <SEO title={post.title} description={post.excerpt} path={`/blog/${post.slug}`} image={post.image} />

      <article className="pt-12">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground" data-testid="blog-back">
            <ArrowLeft className="h-4 w-4" /> Back to blog
          </Link>
          <Reveal>
            <span className="mt-8 inline-block rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">{post.category}</span>
            <h1 className="mt-5 font-display text-4xl font-semibold leading-tight tracking-tight md:text-5xl text-balance">{post.title}</h1>
            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
              <span className="flex items-center gap-2"><User className="h-4 w-4" /> {post.author}</span>
              <span>{post.date}</span>
              <span className="flex items-center gap-2"><Clock className="h-4 w-4" /> {post.readTime}</span>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="mx-auto mt-10 max-w-4xl px-5 sm:px-8">
          <div className="overflow-hidden rounded-3xl border border-border">
            <img src={post.image} alt={post.title} className="h-[24rem] w-full object-cover" />
          </div>
        </Reveal>

        <div className="mx-auto mt-12 max-w-3xl px-5 sm:px-8">
          <div className="space-y-6 text-lg leading-relaxed text-muted-foreground">
            {post.content.map((p, i) => (
              <p key={i} className={i === 0 ? "text-xl text-foreground" : ""}>{p}</p>
            ))}
          </div>
        </div>
      </article>

      {/* Related */}
      <section className="py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <h2 className="font-display text-2xl font-medium tracking-tight md:text-3xl">Keep reading</h2>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {related.map((b) => (
              <Link key={b.slug} to={`/blog/${b.slug}`} className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" data-testid={`related-${b.slug}`}>
                <div className="relative h-40 overflow-hidden">
                  <img src={b.image} alt={b.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <p className="text-xs text-muted-foreground">{b.category} · {b.readTime}</p>
                  <h3 className="mt-2 font-display text-base font-medium leading-snug transition-colors group-hover:text-primary">{b.title}</h3>
                  <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary">Read <ArrowUpRight className="h-3.5 w-3.5" /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <ContactCTA />
    </>
  );
}
